import type { UnifiedTelegramUser } from '@/stores/telegram'

/**
 * 將 Telegram 用戶數據轉換為 DB User 創建輸入
 */
export interface CreateUserFromTelegramInput {
  telegramId: number | string
  firstName: string
  lastName?: string
  username?: string
  photoUrl?: string
  role?: 'student' | 'teacher' | 'admin'
  status?: 'active' | 'pending' | 'banned' | 'suspended' | 'leaved' | 'inactive'
}

/**
 * 將 Telegram 用戶資料轉換為 DB User 創建格式
 */
export function mapTelegramUserToCreateInput(
  telegramUser: UnifiedTelegramUser,
  options: {
    defaultRole?: 'student' | 'teacher' | 'admin'
    defaultStatus?: 'active' | 'pending'
  } = {}
): CreateUserFromTelegramInput {
  const {
    defaultRole = 'student',
    defaultStatus = 'active'
  } = options

  return {
    telegramId: telegramUser.id,
    firstName: telegramUser.first_name,
    lastName: telegramUser.last_name || undefined,
    username: telegramUser.username || undefined,
    photoUrl: telegramUser.photo_url || undefined,
    role: defaultRole,
    status: defaultStatus
  }
}

/**
 * 驗證 Telegram 用戶數據是否足以創建 DB 用戶
 */
export function validateTelegramUserForCreation(
  telegramUser: UnifiedTelegramUser
): { isValid: boolean; missingFields: string[] } {
  const missingFields: string[] = []

  // 檢查必填欄位
  if (!telegramUser.id) {
    missingFields.push('id')
  }
  
  if (!telegramUser.first_name || telegramUser.first_name.trim() === '') {
    missingFields.push('first_name')
  }

  return {
    isValid: missingFields.length === 0,
    missingFields
  }
}

/**
 * 生成用戶顯示名稱
 */
export function generateDisplayName(telegramUser: UnifiedTelegramUser): string {
  const firstName = telegramUser.first_name?.trim()
  const lastName = telegramUser.last_name?.trim()
  const username = telegramUser.username?.trim()

  if (firstName && lastName) {
    return `${firstName} ${lastName}`
  }
  
  if (firstName) {
    return firstName
  }
  
  if (username) {
    return `@${username}`
  }
  
  return `Telegram User ${telegramUser.id}`
}

/**
 * 檢查 Telegram 用戶是否為機器人（不應創建 DB 用戶）
 */
export function isTelegramBot(telegramUser: UnifiedTelegramUser): boolean {
  return telegramUser.is_bot === true
}

/**
 * 從 Telegram 數據生成用戶元數據
 */
export function generateUserMetadata(telegramUser: UnifiedTelegramUser) {
  return {
    telegram_source: telegramUser.source,
    telegram_is_premium: telegramUser.is_premium,
    telegram_language_code: telegramUser.language_code,
    telegram_auth_date: telegramUser.auth_date,
    telegram_raw_data: telegramUser.raw_data,
    created_from_telegram: true,
    created_at_timestamp: Date.now()
  }
}